<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { Banknote, Loader2 } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { API, HOST } from '@/utils/http'

const props = defineProps<{
  open: boolean
  orgId: number
  memberName: string
  receiverCustomerId: number
}>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'paid'): void
}>()

const amount = ref('')
const paying = ref(false)
const error = ref<string | null>(null)
const success = ref(false)

const balance = ref<string | null>(null)
const balanceLoading = ref(false)

const fetchBalance = async () => {
  balanceLoading.value = true
  try {
    const res = await API.get(`${HOST}/payments/bridge/organizations/${props.orgId}/balance`)
    const usdb = res.data?.balances?.find((b: any) => b.currency === 'usdb')
    balance.value = usdb?.balance ?? '0.00'
  } catch {
    balance.value = '0.00'
  } finally {
    balanceLoading.value = false
  }
}

const parsedAmount = computed(() => parseFloat(amount.value) || 0)
const parsedBalance = computed(() => parseFloat(balance.value ?? '0') || 0)

const canPay = computed(() =>
  parsedAmount.value > 0
  && parsedAmount.value <= parsedBalance.value
  && !paying.value
  && !success.value
)

const pay = async () => {
  error.value = null
  paying.value = true
  try {
    await API.post(`${HOST}/payments/bridge/organizations/${props.orgId}/pay-member`, {
      receiver_customer_id: props.receiverCustomerId,
      amount: String(amount.value),
    })
    success.value = true
    emit('paid')
    setTimeout(() => close(), 1500)
  } catch (err: any) {
    error.value = err.response?.data?.detail || 'Payment failed. Please try again.'
  } finally {
    paying.value = false
  }
}

const close = () => {
  emit('update:open', false)
}

const reset = () => {
  amount.value = ''
  error.value = null
  success.value = false
  balance.value = null
}

watch(
  () => props.open,
  (val) => {
    if (val) {
      reset()
      fetchBalance()
    }
  },
  { immediate: true },
)
</script>

<template>
  <Dialog :open="open" @update:open="(val: boolean) => emit('update:open', val)">
    <DialogContent class="sm:max-w-[360px]">
      <DialogHeader>
        <DialogTitle class="flex items-center gap-2">
          <Banknote class="h-4 w-4" />
          Pay {{ memberName }}
        </DialogTitle>
      </DialogHeader>

      <div class="space-y-4 py-2">
        <!-- Balance -->
        <div class="flex items-center justify-between text-xs">
          <span class="text-muted-foreground">Available balance</span>
          <span v-if="balanceLoading" class="text-muted-foreground">Loading...</span>
          <span v-else class="font-medium tabular-nums">{{ parseFloat(balance ?? '0').toFixed(2) }} USD</span>
        </div>

        <!-- Amount -->
        <div class="flex items-center gap-2 justify-end">
          <Input
            v-model="amount"
            type="number"
            min="0.01"
            step="0.01"
            placeholder="0.00"
            class="font-mono w-36"
            :disabled="paying || success"
          />
          <span class="text-sm text-muted-foreground">USD</span>
        </div>

        <div v-if="parsedAmount > 0 && parsedAmount > parsedBalance" class="text-xs text-red-600 text-right">
          Insufficient funds.
        </div>

        <div v-if="error" class="text-xs text-red-600">{{ error }}</div>

        <div v-if="success" class="text-xs text-green-700">
          Payment initiated successfully.
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" size="sm" @click="close" :disabled="paying">Cancel</Button>
        <Button size="sm" @click="pay" :disabled="!canPay">
          <Loader2 v-if="paying" class="h-3.5 w-3.5 mr-1.5 animate-spin" />
          Pay
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
